<template>
<div class="s-layout" :style="colorObject">
  <div class="s-container">
    <Header
      :meta="meta"
      :page-title="getModuleConfig('page_title')"
      :page-image="getModuleConfig('page_image')"
      :page-kv-image="getModuleConfig('page_kv_image')"
      :header-section="getModuleConfig('header_section')"
    />

    <Loading
      v-if="isLoading"
      loading-message="載入中..."
      :background-color="'#ffffff'"
    />

    <img
      class="banner-image w-100"
      v-if="
        deepGet(bookingPreset, 'config.content_page.banner_image') &&
        (
          currentPageNumber === 0 ||
          deepGet(bookingPreset, 'config.content_page.displayed_in_every_page', false)
        )"
      :src="deepGet(bookingPreset, 'config.content_page.banner_image')"
    />

    <div class="page" :style="`background-image: url(${deepGet(bookingPreset, 'config.content_page.background_image', '')})`">
      <div class="p-3 liff-reservation-form">
        <template v-if="currentPageNumber === 0 || deepGet(bookingPreset, 'config.content_page.displayed_in_every_page', false)">
          <div
            v-if="deepGet(bookingPreset, 'config.content_page.form_title')"
            class="page__title"
          >
            {{ deepGet(bookingPreset, 'config.content_page.form_title') }}
          </div>
          <div v-if="deepGet(bookingPreset, 'config.content_page.form_desc')" class="page__desc">
            {{ deepGet(bookingPreset, 'config.content_page.form_desc') }}
          </div>
          <div
            v-if="deepGet(bookingPreset, 'config.content_page.content')"
            v-html="deepGet(bookingPreset, 'config.content_page.content')"
            class="page__content mb-4"
          ></div>
        </template>

        <template v-if="hasDefaultPage && currentPageNumber === 0">

          <SubjectLayout v-if="false === hasSetUpFieldMappingForStartAt" :subject="subjectForStartAt" style="margin-bottom: 24px">
            <DatePicker v-model="start_at" :to-first-second="true" />
          </SubjectLayout>

          <SubjectLayout v-if="false === hasSetUpFieldMappingForEndAt" :subject="subjectForEndAt" style="margin-bottom: 24px">
            <DatePicker v-model="end_at" :to-last-second="true" />
          </SubjectLayout>

        </template>



        <DynamicForm
          ref="dynamicForm"
          v-show="!isLoading"
          :input-subjects="subjectsOfCurrentPageWithoutSection"
          v-model="dynamicFormInput"
          :key="currentPageNumber"
          :validation-filter="onlySubjectsInCurrentPage"
          @outputBranchSelectResult="handleBranchSelectResult"
          @file-uploading="updateFileUploading"
          @diff="handleDynamicFormDataChanged"
          :optionDisabler="optionDisabler"
          :branchFetching="isFetchingBranch"
          :phone-verification-config="phoneVerificationConfig"
        >
        </DynamicForm>

        <b-card
          header-tag="header"
          class="mt-4 liff-reservation-form__card"
          v-for="(section, i) in sectionsOfCurrentPage"
          :key="`section${i}page${currentPageNumber}`"
        >
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">{{ section.title }}</h6>
            </div>
          </template>

          <DynamicForm
            ref="dynamicFormForSection"
            :input-subjects="subjectsOfCurrentSection(section)"
            v-model="dynamicFormInput"
            :key="currentPageNumber"
            @outputBranchSelectResult="handleBranchSelectResult"
            @diff="handleDynamicFormDataChanged"
            :optionDisabler="optionDisabler"
            :phone-verification-config="phoneVerificationConfig"
          ></DynamicForm>
        </b-card>

        <div class="step-indicator my-4" v-if="visiblePages.length > 1">
          <div
            v-for="index in visiblePages.length"
            :key="index"
            class="step-indicator__step"
            :class="{ 'step-indicator__step--filled': index <= currentPageNumber + 1, 'step-indicator__step--current': index === currentPageNumber + 1 }"
          ></div>
        </div>

        <p v-if="failedAvailabilityCheck" class="text-danger text-center mt-4">{{ fullAlertText }}</p>
        <p v-if="!isFetchingLeaveShifts && selectedTimeIsOffDuty" class="text-danger text-center mt-4">該時段涵蓋了非營業時間，請重新選取時間</p>
        <p v-if="startTimeHasInsufficientBookingWindow" class="text-danger text-center mt-4">只能預約{{ bookingWindowConfigForDisplay }}以內的時段</p>
        <p v-if="startTimeHasInsufficientLeadTime" class="text-danger text-center mt-4">{{ leadTimeErrorMessage }}</p>
        <p v-if="startDateIsOnHoliday" class="text-danger text-center mt-4">該店於 {{ dateOfStartAt }} 公休</p>
        <p v-if="!startDateIsOnHoliday && startTimeIsOutOfBusinessHours" class="text-danger text-center mt-4">該店在 {{ dateOfStartAt }} 的營業時間為 {{ businessHoursOfStartDate }}</p>
        <p v-if="endDateIsOnHoliday" class="text-danger text-center mt-4">該店於 {{ dateOfEndAt }} 公休</p>
        <p v-if="!endDateIsOnHoliday && endTimeIsOutOfBusinessHours" class="text-danger text-center mt-4">該店在 {{ dateOfEndAt }} 的營業時間為 {{ businessHoursOfEndDate }}</p>

        <div class="d-flex s-space-x-2 mt-4">
          <SharedButton class="s-btn-outline-primary" v-if="currentPageNumber !== 0" @click="returnToPreviousStep">
            上一步
          </SharedButton>
          <SharedButton
            class="s-btn-bg-primary"
            v-if="currentPageNumber + 1 !== visiblePages.length"
            @click="proceedToNextStep"
            :disabled="showingLoadingForNextStepButton || bookingIsInvalid"
            :isLoading="showingLoadingForNextStepButton"
          >
            下一步
          </SharedButton>
          <SharedButton
            class="s-btn-bg-primary"
            v-if="currentPageNumber + 1 === visiblePages.length"
            @click="submit"
            :disabled="!isCanEdit || showingLoadingForNextStepButton || bookingIsInvalid"
            :isLoading="showingLoadingForNextStepButton"
          >
            {{ deepGet(bookingPreset, 'config.content_page.submit_button_text') }}
          </SharedButton>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import bookingApi from '@/apis/liff/v2/booking'
import bookingPresetApi from '@/apis/liff/v2/booking-preset'
import DatePicker from "@/components/AppDatePicker.vue";
import DynamicForm from "@/components/DynamicForm/DynamicForm.vue";
import SubjectLayout from "@/components/DynamicForm/SubjectLayout.vue";
import SharedButton from '@/components/Page/Liff/Shared/Button.vue';
import Header from '@/components/Page/Liff/Shared/HeaderV3'
import Loading from "@/components/Page/Liff/Shared/Loading";
import { themeColorFn } from "@/mixins/liff/themeColor";
import BookingPresetDataModel from "@/models/BookingPreset.model"
import DynamicFormField from '@/models/DynamicFormField.model'
import { passesVisibleCondition } from "@/utils/DynamicFormUtils"
import isEmpty from "@/utils/isEmpty";
import BookingBusinessLogic from '@/utils/booking/BookingBusinessLogic'
import BookingShift from '@/utils/booking/BookingShift'
import BookingWindow from '@/utils/booking/BookingWindow'
import LeadTime from '@/utils/booking/LeadTime'
import OccupancyChecker from "@/utils/booking/OccupancyChecker";
import { generateModuleConfigGetter } from "@/utils/liff/vueStoreHelpers";
import _ from "lodash";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex';

const bookingBusinessLogic = new BookingBusinessLogic()
const leaveShiftChecker = Vue.observable(new BookingShift())
const bookingWindowChecker = new BookingWindow()
const leadTimeChecker = new LeadTime()

const isBranchOnlyOccupyMode = (occupyConfig) => {
  if (!occupyConfig || true !== _.get(occupyConfig, 'enable', false)) {
    return false
  }
  return (
    true === _.get(occupyConfig, 'is_check_branch', false) &&
    true !== _.get(occupyConfig, 'is_check_staff', false) &&
    true !== _.get(occupyConfig, 'is_check_service', false) &&
    true !== _.get(occupyConfig, 'is_check_collection', false)
  )
}

const fullAlertTextFromBookingPreset = (bookingPreset) => {
  const presetOccupyConfig = _.get(bookingPreset, 'config.occupy_config', null)
  const branchOccupyConfig = _.get(bookingPreset, 'branch_config.occupy_config', null)
  const useBranchCustomConfig = (
    isBranchOnlyOccupyMode(presetOccupyConfig) &&
    Number(_.get(branchOccupyConfig, 'type', 0)) === 1
  )
  const effectiveAlertText = useBranchCustomConfig
    ? _.get(branchOccupyConfig, 'full_alert_text', _.get(presetOccupyConfig, 'full_alert_text', ''))
    : _.get(presetOccupyConfig, 'full_alert_text', '')
  return (effectiveAlertText && String(effectiveAlertText).trim())
    ? effectiveAlertText
    : '該時段的預約數量已達上限'
}

export default {
  mixins: [
    themeColorFn({ themeConfigPage: 'booking' })
  ],
  components: {
    DatePicker,
    DynamicForm,
    SubjectLayout,
    SharedButton,
    Header,
    Loading,
  },
  computed: {
    ...mapGetters({
      meta: "liffGeneral/meta",
      user: 'liffAuth/user',
    }),
    bookingId() {
      return this.$route.params.bookingId
    },
    bookingPresetId() {
      return this.$route.params.bookingPresetId
    },
    dataModel() {
      return new BookingPresetDataModel(this.bookingPreset)
    },
    sections() {
      return _.get(this.bookingPreset, 'config.sections', []);
    },
    pages() {
      return _.get(this.bookingPreset, 'config.pages', []);
    },
    hasDefaultPage() {
      const visible = subjectOrSection => isEmpty(subjectOrSection.page_id) && this.passesVisibleCondition(subjectOrSection);
      return this.allSubjects.filter(visible).filter(subject => false === _.get(subject, 'dashboard_only', false)).length > 0
        || this.sections.filter(visible).length > 0
        || false === this.hasSetUpFieldMappingForStartAt
        || false === this.hasSetUpFieldMappingForEndAt;
    },
    visiblePages() {
      const pagesFromConfig = _.sortBy(this.pages.filter(this.passesVisibleCondition), 'order');
      return this.hasDefaultPage ? [{ _id: null, title: null, order: 0 }, ...pagesFromConfig] : pagesFromConfig;
    },
    subjectsOfCurrentPageWithoutSection() {
      return this.allSubjects.filter(subject => {
        return isEmpty(subject.section_id)
          && (isEmpty(this.pageIdOfCurrentPage) ? isEmpty(subject.page_id) : subject.page_id === this.pageIdOfCurrentPage)
          && this.passesVisibleCondition(subject)
          && false === _.get(subject, 'dashboard_only', false)
      });
    },
    sectionsOfCurrentPage() {
      return this.sections.filter(section => {
        return (isEmpty(this.pageIdOfCurrentPage) ? isEmpty(section.page_id) : section.page_id === this.pageIdOfCurrentPage)
          && this.passesVisibleCondition(section)
      });
    },
    pageIdOfCurrentPage() {
      return _.get(this.visiblePages[this.currentPageNumber], '_id', null);
    },
    hasSetUpFieldMappingForStartAt() {
      return this.dataModel.hasSetUpFieldMappingForStartAt
    },
    hasSetUpFieldMappingForEndAt() {
      return this.dataModel.hasSetUpFieldMappingForEndAt
    },
    hasSetUpFieldMappingForBranchId() {
      return this.dataModel.branchFieldId !== undefined
    },
    isCanEdit() {
      const startAt = _.get(this.bookingPreset, 'start_at')
      const endAt = _.get(this.bookingPreset, 'end_at')

      // 如果有設定開始時間且目前時間早於開始時間，則不可編輯
      if (startAt && moment().isBefore(startAt)) {
        return false
      }

      // 如果有設定結束時間且目前時間晚於結束時間，則不可編輯
      if (endAt && moment().isAfter(endAt)) {
        return false
      }

      if (this.booking && this.bookingPreset && this.bookingPreset?.config?.can_edit_at_liff_mapping) {
        return this.bookingPreset.config.can_edit_at_liff_mapping.includes(this.booking.status);
      } else {
        return true
      }
    },
    startAtFieldId() {
      return this.dataModel.startAtFieldId
    },
    endAtFieldId() {
      return this.dataModel.endAtFieldId
    },
    shopFieldId() {
      return this.dataModel.branchFieldId
    },
    valueOfStartAt() {
      if (!this.hasSetUpFieldMappingForStartAt) {
        return this.start_at;
      }
      return this.dynamicFormInput[this.startAtFieldId];
    },
    valueOfEndAt() {
      if (!this.hasSetUpFieldMappingForEndAt) {
        return this.end_at;
      }
      return this.dynamicFormInput[this.endAtFieldId];
    },
    valueOfShopId() {
      if (!this.hasSetUpFieldMappingForBranchId) {
        return null;
      }
      return _.get(this.dynamicFormInput[this.shopFieldId], 'value');
    },
    selectedTimeIsOffDuty() {
      return this.valueOfStartAt &&
        this.valueOfEndAt &&
        leaveShiftChecker.isPartiallyOffDutyBetween(this.valueOfStartAt, this.valueOfEndAt)
    },
    occupancyCheckCollectionId() {
      return _.get(this.bookingPreset, 'config.occupy_config.collection_id', null);
    },
    failedAvailabilityCheck() {
      return this.occupancyChecker &&
        true === this.occupancyChecker.hasPerformedAvailabilityCheck &&
        false === this.occupancyChecker.getAvailabilityResult()
    },
    fullAlertText() {
      return fullAlertTextFromBookingPreset(this.bookingPreset)
    },
    startTimeHasInsufficientBookingWindow() {
      return this.valueOfStartAt && this.hasInsufficientBookingWindow(this.valueOfStartAt)
    },
    startTimeHasInsufficientLeadTime() {
      return this.valueOfStartAt && this.hasInsufficientLeadTime(this.valueOfStartAt)
    },
    startTimeIsOutOfBusinessHours() {
      return this.valueOfStartAt && false === this.dataModel.targetTimeIsBetweenBusinessHours(this.valueOfStartAt)
    },
    startDateIsOnHoliday() {
      return this.valueOfStartAt && this.dataModel.isOfficialHolidayOfBranch(moment(this.valueOfStartAt).isoWeekday())
    },
    endTimeIsOutOfBusinessHours() {
      return this.valueOfEndAt && false === this.dataModel.targetTimeIsBetweenBusinessHours(this.valueOfEndAt)
    },
    endDateIsOnHoliday() {
      return this.valueOfEndAt && this.dataModel.isOfficialHolidayOfBranch(moment(this.valueOfEndAt).isoWeekday())
    },
    dateOfStartAt() {
      return this.valueOfStartAt ? moment(this.valueOfStartAt).format('YYYY-MM-DD') : ''
    },
    dateOfEndAt() {
      return this.valueOfEndAt ? moment(this.valueOfEndAt).format('YYYY-MM-DD') : ''
    },
    businessHoursOfStartDate() {
      return (this.dataModel.businessHoursOfBranchConfig(moment(this.valueOfStartAt).isoWeekday()) || [])
        .map(businessHour => `${businessHour.start_time} ~ ${businessHour.end_time}`).join(', ')
    },
    businessHoursOfEndDate() {
      return (this.dataModel.businessHoursOfBranchConfig(moment(this.valueOfEndAt).isoWeekday()) || [])
        .map(businessHour => `${businessHour.start_time} ~ ${businessHour.end_time}`).join(', ')
    },
    bookingIsInvalid() {
      return [
        this.failedAvailabilityCheck,
        this.selectedTimeIsOffDuty,
        this.startTimeHasInsufficientBookingWindow,
        this.startTimeHasInsufficientLeadTime,
        this.startDateIsOnHoliday,
        this.startTimeIsOutOfBusinessHours,
        this.endDateIsOnHoliday,
        this.endTimeIsOutOfBusinessHours,
      ].some(condition => condition === true)
    },
    bookingWindowConfigForDisplay() {
      return _.get({
        day: ' ' + this.dataModel.bookingWindowDays + ' 天',
        minute: ' ' + this.dataModel.bookingWindowMinutes + ' 分鐘',
      }, this.dataModel.bookingWindowType)
    },
    leadTimeErrorMessage() {
      if (this.dataModel.leadTimeType === 'minute' && this.dataModel.leadTimeMinutes === 1) {
        return '預約時間須晚於當前時間'
      } else {
        return '必須提前' + _.get({
          day: ' ' + this.dataModel.leadTimeDays + ' 天',
          minute: ' ' + this.dataModel.leadTimeMinutes + ' 分鐘',
        }, this.dataModel.leadTimeType) + '進行預約'
      }
    },
    showingLoadingForNextStepButton() {
      return (this.occupancyChecker && (
          true === this.occupancyChecker.isCheckingOccupancy ||
          true === this.occupancyChecker.isCheckingAvailability
        )) ||
        this.isFetchingLeaveShifts ||
        this.isFileUploading
    },
    occupancyCollectionField() {
      return this.allSubjects.find(subject => subject.collection_id === this.occupancyCheckCollectionId)
    },
    lastOccupancyConditionType() {
      const conditionFields = [
        {
          subject: this.allSubjects.find(subject => subject._id === this.shopFieldId),
          type: 'shop'
        },
        {
          subject: this.occupancyCollectionField,
          type: 'collection'
        }
      ]

      const maxOrderField = _.maxBy(conditionFields, 'subject.order')
      return _.get(maxOrderField, 'type', 'time')
    },
    isFetchingLeaveShifts() {
      return leaveShiftChecker.isFetchingShifts
    },
    startTimeFieldConbinedWithBranchConfig() {
      const startAtDefaultField = this.dataModel.baseStartTimeField
      const startAtField = this.dataModel.branchStartTimeField || startAtDefaultField
      if (!startAtField) {
        return null
      }

      const pickConfiguredValue = (key, fallback = null) => {
        const branchValue = _.get(this.dataModel.branchStartTimeField, key, undefined)
        const baseValue = _.get(startAtDefaultField, key, undefined)

        if (branchValue !== undefined && branchValue !== null && branchValue !== '' && branchValue !== 'default') {
          return branchValue
        }
        if (baseValue !== undefined && baseValue !== null) {
          return baseValue
        }
        return fallback
      }

      const resolvedOptions = (() => {
        const branchOptions = _.get(this.dataModel.branchStartTimeField, 'options', [])
        const baseOptions = _.get(startAtDefaultField, 'options', [])
        const sourceOptions = Array.isArray(branchOptions) && branchOptions.length > 0 ? branchOptions : baseOptions

        return sourceOptions.map(option => ({
          text: option.text,
          value: option.value || option.text, // NOTE: 有一些日期選項的舊資料只有 text 沒有 value
          time_options: _.get(option, 'time_options', []),
        }))
      })()

      return {
        ...startAtField,
        disabled: !this.isCanEdit || (this.dataModel.branchFieldIdForStartAtOptions && !this.dynamicFormInput[this.dataModel.branchFieldIdForStartAtOptions]),
        id: startAtField._id,
        format: pickConfiguredValue('format', 'datetime'),
        date_condition: pickConfiguredValue('date_condition', 'unlimited'),
        time_condition: pickConfiguredValue('time_condition', 'unlimited'),
        time_style: pickConfiguredValue('time_style', 'select_style'),
        date_style: pickConfiguredValue('date_style', 'calendar_style'),
        daily_time: pickConfiguredValue('daily_time', null),
        time_options: pickConfiguredValue('time_options', []),
        options: resolvedOptions,
      }
    },
    allSubjects() {
      return DynamicFormField.fromArray(_.get(this.bookingPreset, 'config.fields', [])).map(fieldModel => {
        if (fieldModel._id === _.get(this.startTimeFieldConbinedWithBranchConfig, 'id')) {
          return this.startTimeFieldConbinedWithBranchConfig
        }

        return {
          ...fieldModel.flattenedAttributes(),
          disabled: !this.isCanEdit || (
            this.dataModel.startAtFieldId == fieldModel._id &&
            this.dataModel.branchFieldIdForStartAtOptions &&
            [null, undefined].includes(this.dynamicFormInput[this.dataModel.branchFieldIdForStartAtOptions])
          )
        }
      })
    },
    phoneVerificationConfig() {
      return {
        enabled: this.dataModel.phoneVerificationTiming === 'current_page',
        field_id: this.dataModel.phoneVerificationFieldId
      }
    },
    phoneVerificationIsRequired() {
      return this.phoneVerificationConfig.enabled && this.phoneVerificationConfig.field_id
    },
  },
  data() {
    return {
      bookingPreset: {},
      start_at: null,
      end_at: null,
      dynamicFormInput: {},
      isLoading: true,
      subjectForStartAt: {
        id: uuidv4(),
        title: '開始時間',
        required: true,
        memo: null,
      },
      subjectForEndAt: {
        id: uuidv4(),
        title: '結束時間',
        required: true,
        memo: null,
      },
      currentPageNumber: 0,
      isFileUploading: false,
      booking: false,
      isFetchingBranch: false,
      occupancyChecker: null,
      branchPresetRequestId: 0,
    }
  },
  async mounted() {
    this.isLoading = true
    await this.fetchFields()
    this.occupancyChecker = new OccupancyChecker(
      this.bookingPresetId,
      _.get(this.bookingPreset, 'config.occupy_config'),
      _.get(this.bookingPreset, 'branch_config.occupy_config', null),
    )
    this.occupancyChecker.setCollectionIsBoundToBranch(
      false === _.isEmpty(_.get(this.occupancyCollectionField, 'branch_field_id'))
    )
    this.occupancyChecker.setLastConditionType(this.lastOccupancyConditionType)

    _.invoke({
      day: () => bookingWindowChecker.setWindowByDays(this.dataModel.bookingWindowDays),
      minute: () => bookingWindowChecker.setWindowByMinutes(this.dataModel.bookingWindowMinutes),
    }, this.dataModel.bookingWindowType)

    _.invoke({
      day: () => leadTimeChecker.setLeadDays(this.dataModel.leadTimeDays),
      minute: () => leadTimeChecker.setLeadMinutes(this.dataModel.leadTimeMinutes),
    }, this.dataModel.leadTimeType)
  },
  methods: {
    deepGet: _.get,
    ...mapActions('liffBooking', ['setSubmittedBooking', 'setViewingBookingPreset']),
    getModuleConfig: generateModuleConfigGetter('liff_booking'),
    updateFileUploading(val) {
      this.isFileUploading = val
    },
    async fetchBookingPresetByBranchId(branchId) {
      const requestId = ++this.branchPresetRequestId
      const bookingPreset = await bookingPresetApi.get(this.bookingPresetId, { branch_id: branchId })
      if (requestId !== this.branchPresetRequestId) {
        return null
      }
      this.bookingPreset = bookingPreset
      return bookingPreset
    },
    async handleBranchSelectResult(value) {
      this.checkLeaveShiftsForSelectedShop()
      // 專門 for 預約開始時間要根據所選分店設定動態調整
      this.isFetchingBranch = true
      if (this.dataModel.branchFieldIdForStartAtOptions == value.fieldId && this.hasSetUpFieldMappingForStartAt) {
        const bookingPreset = await this.fetchBookingPresetByBranchId(value.branchId)
        if (!bookingPreset) {
          this.isFetchingBranch = false
          return
        }
        if (this.occupancyChecker) {
          this.occupancyChecker.setBranchOccupyConfig(_.get(bookingPreset, 'branch_config.occupy_config', null))
          if (this.valueOfShopId === value.branchId) {
            await this.occupancyChecker.receiveShop(value.branchId)
          }
        }
      }
      this.isFetchingBranch = false
    },
    passesVisibleCondition(subjectOrSectionOrPage) {
      return passesVisibleCondition(_.get(subjectOrSectionOrPage, 'visible_when_any', []), this.allSubjects, this.dynamicFormInput)
    },
    subjectsOfCurrentSection(section) {
      return this.allSubjects.filter(subject => {
        return subject.section_id === section._id
          && this.passesVisibleCondition(subject)
          && false === _.get(subject, 'dashboard_only', false)
      });
    },
    onlySubjectsInCurrentPage(subject) {
      return this.subjectsOfCurrentPageWithoutSection.some(sub => sub._id === subject._id) ||
        this.sectionsOfCurrentPage.some(section => this.subjectsOfCurrentSection(section).some(sub => sub._id === subject._id))
    },
    async handleDynamicFormDataChanged(newValue, changedSubject) {
      if (this.valueOfStartAt && this.valueOfEndAt && this.valueOfShopId) {
        this.fetchLeaveShifts()
      }

      if (changedSubject._id === this.startAtFieldId && changedSubject._id === this.endAtFieldId && this.valueOfStartAt && this.valueOfEndAt) {
        const startAt = newValue
        const endAt = bookingBusinessLogic.endDateTimeToSaveInGeneral(newValue).format('YYYY-MM-DD HH:mm:ss')
        _.invoke(this.occupancyChecker, 'receiveTimeRange', startAt, endAt)
        return
      }

      if (changedSubject._id === this.startAtFieldId && this.valueOfStartAt) {
        const endAtField = this.dataModel.endTimeField
        const endAtFieldIsDashboardOnly = (new DynamicFormField(endAtField)).dashboard_only

        if (endAtField && !endAtFieldIsDashboardOnly) {
          _.invoke(this.occupancyChecker, 'receiveStartAt', this.valueOfStartAt)
          return
        } else {
          const startAt = newValue
          const endAt = bookingBusinessLogic.endDateTimeToSaveInGeneral(newValue).format('YYYY-MM-DD HH:mm:ss')
          _.invoke(this.occupancyChecker, 'receiveTimeRange', startAt, endAt)
          return
        }
      }

      if (changedSubject._id === this.endAtFieldId && this.valueOfEndAt) {
        _.invoke(this.occupancyChecker, 'receiveEndAt', bookingBusinessLogic.endDateTimeToSaveInGeneral(this.valueOfEndAt).format('YYYY-MM-DD HH:mm:ss'))
        return
      }

      if (changedSubject._id === this.shopFieldId) {
        const shopId = _.get(newValue, 'value')
        if (isBranchOnlyOccupyMode(_.get(this.bookingPreset, 'config.occupy_config'))) {
          this.isFetchingBranch = true
          try {
            let bookingPreset = null
            if (shopId) {
              bookingPreset = await this.fetchBookingPresetByBranchId(shopId)
              if (!bookingPreset || this.valueOfShopId !== shopId) {
                return
              }
            }
            if (this.occupancyChecker) {
              this.occupancyChecker.setBranchOccupyConfig(
                shopId ? _.get(bookingPreset, 'branch_config.occupy_config', null) : null,
              )
            }
          } finally {
            this.isFetchingBranch = false
          }
        } else if (this.occupancyChecker) {
          this.occupancyChecker.setBranchOccupyConfig(null)
        }
        if (this.valueOfShopId !== shopId) {
          return
        }
        await _.invoke(this.occupancyChecker, 'receiveShop', shopId)
        return
      }

      if (changedSubject.collection_id === this.occupancyCheckCollectionId) {
        _.invoke(this.occupancyChecker, 'receiveCollectionItem', _.get(newValue, '[0].collection_item_id'))
        return
      }
    },
    async fetchFields() {
      // 重新獲取最新的會員資料，避免使用快取的舊資料
      try {
        await this.$store.dispatch('liffAuth/fetchMe')
      } catch (error) {
        console.error('[Form.vue] 獲取會員資料失敗:', error)
      }

      this.bookingPreset = await bookingPresetApi.get(this.bookingPresetId);
      bookingBusinessLogic.setBookingPresetDataModel(this.dataModel)
      if (this.bookingPreset.status != 'published') {
        return this.$router.push({ name: "LiffBookingClosed" })
      }
      if (this.dataModel.requireCustomerRegistration() && !this.user.registered_at) {
        return this.$router.push({ name: "LiffRegisterIndex" })
      }
      if (this.dataModel.isWizardMode) {
        return this.$router.push({ name: "LiffReservation" })
      }

      if (this.bookingId) {
        this.booking = await bookingApi.getDetail({ id: this.bookingId })
        _.get(this.booking, 'data', []).map((field) => {
          this.dynamicFormInput[field.field_id] = field.value
          if (this.dataModel.branchFieldIdForStartAtOptions === field.field_id) {
            this.handleBranchSelectResult({ fieldId: field.field_id, branchId: field.value.value, type: 'shop' })
          }
        })
        if (this.booking?.branch_id && isBranchOnlyOccupyMode(_.get(this.bookingPreset, 'config.occupy_config'))) {
          await this.fetchBookingPresetByBranchId(this.booking.branch_id)
        }
      }

      this.allSubjects.forEach(subject => {
        if (subject.assign_to_customer_data && [null, undefined].includes(this.dynamicFormInput[subject._id])) {
          this.$set(this.dynamicFormInput, subject._id, _.get(this.user, subject.assign_to_customer_data))
        }
      })

      this.$nextTick(this.prepareAsyncDynamicFormOptions)

      this.isLoading = false
    },
    async validateSubjectsOfCurrentPage() {
      const validationForSubjectsWithoutSection = await this.$refs.dynamicForm.validate()

      let validationForSubjectsInSections = []
      if (this.$refs.dynamicFormForSection) {
        validationForSubjectsInSections = await Promise.all(this.$refs.dynamicFormForSection.map(formForSection => formForSection.validate()))
      }

      return true === validationForSubjectsWithoutSection && validationForSubjectsInSections.every(Boolean)
    },
    async prepareAsyncDynamicFormOptions() {
      const promises = [this.$refs.dynamicForm.fetchAllAsyncOptions()];

      if (this.$refs.dynamicFormForSection) {
        promises.push(Promise.all(
          this.$refs.dynamicFormForSection.map(dynamicForm =>
            dynamicForm.fetchAllAsyncOptions())
        ));
      }

      await Promise.all(promises);
    },
    async checkLeaveShiftsForSelectedShop() {
      if (!this.valueOfShopId) {
        return
      }
      await this.fetchLeaveShifts()
    },
    async fetchLeaveShifts() {
      await leaveShiftChecker.fetch(this.bookingPresetId, this.valueOfShopId, this.valueOfStartAt, this.valueOfEndAt)
    },
    returnToPreviousStep() {
      this.currentPageNumber--

      this.$nextTick(this.prepareAsyncDynamicFormOptions)
    },
    async proceedToNextStep() {
      if (false === await this.validateSubjectsOfCurrentPage()) {
        return
      }
      this.currentPageNumber++

      this.$nextTick(this.prepareAsyncDynamicFormOptions)
    },
    async submit() {
      if (false === await this.validateSubjectsOfCurrentPage()) {
        return
      }

      this.isLoading = true
      try {
        const dataToSave = {
          preset_id: this.bookingPresetId,
          fields: this.dynamicFormInput,
        }

        if (false === this.hasSetUpFieldMappingForStartAt) {
          dataToSave.start_at = this.start_at
        }

        if (false === this.hasSetUpFieldMappingForEndAt) {
          dataToSave.end_at = this.end_at
        }

        // 建立 save_to_customer mapping
        const saveToCustomer = {}
        this.allSubjects.forEach(subject => {
          if (subject.save_to_customer_data && this.dynamicFormInput[subject._id]) {
            saveToCustomer[subject._id] = subject.save_to_customer_data
          }
        })

        // 只有當有欄位需要儲存到 customer 時才加入
        if (Object.keys(saveToCustomer).length > 0) {
          dataToSave.save_to_customer = saveToCustomer
        }
        if (this.bookingId) {
          const res = await bookingApi.updateBooking(this.bookingId, dataToSave)
          this.setSubmittedBooking(res.data)
        } else {
          const res = await bookingApi.saveBooking(dataToSave)
          this.setSubmittedBooking(res.data)
        }
        this.setViewingBookingPreset(this.bookingPreset)
        this.$router.push({ name: "LiffBookingFinished", params: { bookingPresetId: this.bookingPresetId } })
      } catch (e) {
        console.error(e)
        const errorMessage = _.get(e, 'response.data.message') || _.get(e, 'message') || '送出失敗，請稍後再試'
        this.$swal.fire({ title: errorMessage, type: "error" })
        this.isLoading = false
      }
    },
    hasInsufficientBookingWindow(datetime) {
      if ('none' === this.dataModel.bookingWindowType) {
        return false // 設為不限制時，不檢查
      }
      return false === _.defaultTo(bookingWindowChecker.isAvailable(datetime), false)
    },
    hasInsufficientLeadTime(datetime) {
      return false === _.defaultTo(leadTimeChecker.isAvailable(datetime), false)
    },
    optionDisabler(subject, option) {
      if (subject.type === 'datetime') {
        const { date, time } = option

        if (subject.date_condition === 'limited' && time === null) { // determining date
          return leaveShiftChecker.isTotallyOffDutyBetween(moment(date).startOf('day'), moment(date).endOf('day').startOf('second')) ||
            this.dataModel.isOfficialHolidayOfBranch(moment(date).isoWeekday())
        }

        if (subject.time_condition === 'limited' && time !== null) { // determining time
          const startAt = moment(`${date} ${time}`)
          return !!date && [
            leaveShiftChecker.isPartiallyOffDutyBetween(startAt, startAt),
            this.hasInsufficientBookingWindow(startAt),
            this.hasInsufficientLeadTime(startAt),
            false === this.dataModel.targetTimeIsBetweenBusinessHours(startAt),
          ].some(condition => condition === true)
        }
      }

      if (subject.type === 'date' && subject.date_condition === 'limited') {
        return this.dataModel.isOfficialHolidayOfBranch(moment(option).isoWeekday())
      }

      if (!this.occupancyChecker) {
        return false
      }
      const occupancyResult = this.occupancyChecker.getOccupancyResult()
      const type = _.get(occupancyResult, 'type')
      const disabledOptions = _.get(occupancyResult, 'disabled', [])

      if (type === 'collection') {
        const isDisabled = subject.collection_id === this.occupancyCheckCollectionId && disabledOptions.includes(_.get(option, 'value.collection_item_id'))

        if (!isDisabled) {
          return false
        }

        return {
          disabled: isDisabled,
          reason: '該時段已達預約上限'
        }
      }

      if (type === 'shop') {
        return subject._id === this.shopFieldId && disabledOptions.includes(_.get(option, 'value'))
      }

      return false // do not disable option by default
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/css/liff/main.css";
@import "../../../assets/css/liff/liff_init_theme_config.css";

.s-layout {
  color: var(--liff-primary_text_color);
  background-color: var(--liff-layout_bg_color);
  --s-gray: #979797 !important;
  --s-gray-light: #f0f0f0 !important;
  --s-danger: #fe0000;
}

.s-container {
  min-height: 100vh;
}

.page {
  padding: 16px 12px;
  background-color: var(--liff-content_bg_color);
  background-size: 100% auto;
  background-repeat: repeat-y;

  &__title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 500;
    margin-bottom: 16px;
    text-align: center;
    color: var(--liff-primary_text_color) !important;
  }

  &__desc {
    font-size: 14px;
    line-height: 22px;
    color: var(--liff-secondary_text_color) !important;
    margin-bottom: 16px;
    text-align: center;

    b {
      color: var(--primary-color) !important;
      font-weight: 600;
    }
  }

  &__button {
    padding: 12px;
    color: var(--liff-primary_text_color) !important;
  }

  &__content::v-deep{
    a{
      color: var(--primary-color) !important;
      text-decoration: underline !important;
    }
  }
}

.liff-reservation-form {
  &__card {
    border-radius: 10px;
    border: none;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    .card-header {
      border-radius: 10px 10px 0 0;
      border-bottom: 1px solid #E5E5EA;
      font-size: 16px;
      font-weight: 500;
      padding: 16px 16px 12px 16px;
    }
  }
  &__textarea {
    border-radius: 10px;
    border: 1px solid #636366;
    color: #2C2C2E;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    padding: 16px 12px;
  }

  input {
    font-size: 14px;
    height: auto;
    line-height: 1.2;
    border-bottom: 1px solid #E5E5EA;
    padding-bottom: 6px;
    }
  }

.step-indicator {
  display: flex;
  justify-content: center;
  > * + * {
    margin-left: 12px;
  }
  &__step {
    width: 10px;
    height: 10px;
    border-radius: 100%;
    border: 1px solid var(--liff-button-color);
    &--filled {
      background-color: var(--liff-button-color);
    }
    &--current {
      position: relative;
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 20px;
        height: 20px;
        border-radius: 100%;
        border: 1px solid var(--liff-button-color);
        transform: translate(-50%, -50%);
      }
    }
  }
}
</style>
